import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ReviewService } from '../../../services/review.service';
import { CommonFunctionsService } from '../../../services/common-functions.service';
import { CommonModule } from '@angular/common';
import { Constants } from '../../../constants/constants';
import { ReviewDetails } from '../../../models/ReviewDetails';
import { Subject, takeUntil } from 'rxjs';
import { CommonDataService } from '../../../services/common-data.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ExportObjectAsFieldValuePDFOptions } from '../../../models/ExportObjectAsFieldValuePDFOptions';
import { ExportTablesToPDFOptions } from '../../../models/ExportTableToPDF';
import { CostReviewPlannedCosts } from '../../../models/CostReviewPlannedCosts';
import { CostReviewAttributes } from '../../../models/CostReviewAttributes';
import { CostReviewSummary } from '../../../models/CostReviewSummary';
import { CostReviewPrefab } from '../../../models/CostReviewPrefab';
import { CostReviewMarginExceptions } from '../../../models/CostReviewMarginExceptions';
import { SolPolicyExceptionData } from '../../../models/SolPolicyExceptionData';
import { DecimalPrecisionPipe } from '../../../pipes/decimal-precision/decimal-precision.pipe';
import { MultilingualCurrencyPipe } from '../../../pipes/multilingual-currency/multilingual-currency.pipe';
import { CostReviewDatabaseRecord } from '../../../models/CostReviewDatabaseRecord';

@Component({
  selector: 'app-sol-policy-exception-tab',
  imports: [
    CommonModule,
    MatTableModule,
    TranslatePipe,
    DecimalPrecisionPipe
],
  providers: [DecimalPrecisionPipe, MultilingualCurrencyPipe],
  templateUrl: './sol-policy-exceptions-tab.component.html',
  styleUrl: './sol-policy-exceptions-tab.component.scss'
})
export class SolPolicyExceptionTabComponent implements OnInit {
  constants = Constants;
  columns = [
    { field: 'itemName', header: 'REVIEW_SOL_POLICY_EXCEPTIONS_TAB.ITEM', isNumber: false, isCenterAlign: false },
    { field: 'actual', header: 'REVIEW_SOL_POLICY_EXCEPTIONS_TAB.ACTUAL', isNumber: true, isCenterAlign: true },
    { field: 'policy', header: 'REVIEW_SOL_POLICY_EXCEPTIONS_TAB.POLICY', isNumber: true, isCenterAlign: true },
    { field: 'variance', header: 'REVIEW_SOL_POLICY_EXCEPTIONS_TAB.VARIANCE', isNumber: true, isCenterAlign: true },
    { field: 'approver', header: 'REVIEW_SOL_POLICY_EXCEPTIONS_TAB.APPROVER', isNumber: false, isCenterAlign: false },
    { field: 'approverEmailId', header: 'REVIEW_SOL_POLICY_EXCEPTIONS_TAB.APPROVER_EMAIL', isNumber: false, isCenterAlign: false },
    { field: 'reasonForException', header: 'REVIEW_SOL_POLICY_EXCEPTIONS_TAB.REASON_FOR_EXCEPTION', isNumber: false, isCenterAlign: false }
  ];
  displayedColumns = this.columns.map(col => col.field);
  costReviewProjectMgmtRequirements: SolPolicyExceptionData[] = [];
  costPlannedCostLabor: CostReviewPlannedCosts[] = [];
  costMarginExceptions: CostReviewMarginExceptions | undefined;
  costSummary: CostReviewSummary = new CostReviewSummary();
  costPrefabPanel: CostReviewPrefab | undefined;
  dataSourceItem = new MatTableDataSource(this.costReviewProjectMgmtRequirements);
  engineerReasonForException: string = '';
  engineerApproverEmail: string = '';
  business: string = '';
  isEngineerExceptionApplicable: boolean = false;
  isDataAvailable: boolean = false;
  destroy$ = new Subject<void>();
  @Output() disablePdf = new EventEmitter<boolean>();
  constructor(private commonDataService: CommonDataService, 
    private commonFunctionsService: CommonFunctionsService, 
    private readonly reviewService: ReviewService, 
    private translate: TranslateService, 
    private readonly multilingualCurrencyPipe: MultilingualCurrencyPipe,
    private readonly decimalPrecision: DecimalPrecisionPipe) {
  }

  ngOnInit(): void {
    this.initializePolicyExceptionData();
    this.commonDataService.triggerTabMethod$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.exportToPdf(this.reviewService.selectedReview, Constants.SOL_POLICY_EXCEPTIONS);
      });
    this.sendDisablePdfEvent();
  }
  sendDisablePdfEvent() {
    this.disablePdf.emit(false);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializePolicyExceptionData() {
    this.business = this.reviewService.getBusiness();
    const reviewData: CostReviewDatabaseRecord = this.reviewService.costReviewDetails;
    if (!reviewData?.reviewObject) return;
    this.costPlannedCostLabor = reviewData?.reviewObject.costReviewPlannedCosts || [];
    this.costMarginExceptions = reviewData?.reviewObject.costReviewMarginExceptions || undefined;
    this.costSummary = reviewData?.reviewObject.costReviewSummary;
    if (this.business === this.constants.BUSINESS_AUTOMATION || this.business === this.constants.BUSINESS_MECHANICAL) {
      const businessWiseData = reviewData.reviewObject as CostReviewAttributes;
      this.costPrefabPanel = businessWiseData.costReviewPrefab;
      this.engineerReasonForException = businessWiseData?.engineerReasonForException || this.constants.EMPTY_STRING;
      this.engineerApproverEmail = businessWiseData?.engineerApproverEmail || this.constants.EMPTY_STRING;
      this.isEngineerExceptionApplicable = businessWiseData?.isEngineerExceptionApplicable || false;
    }
    this.formatPolicyExceptionData();
    this.isDataAvailable = this.costReviewProjectMgmtRequirements.length > this.constants.ZERO;
  }

  formatPolicyExceptionData() {
    let costPlannedCostLaborEngineerObj = this.costPlannedCostLabor?.find((item) => item.itemName === this.constants.COST_LABOR_ENGINEER);

    if (this.costMarginExceptions?.estimatedMarginVariance && this.costMarginExceptions?.estimatedMarginVariance < this.constants.ZERO) {
      const margin = this.costMarginExceptions.estimatedMargin ?? 0;
      const minimumMargin = this.costMarginExceptions.estimatedMarginMinimum ?? 0;
      const variance = margin - minimumMargin;
      let policyExceptionObj: SolPolicyExceptionData = new SolPolicyExceptionData();
      policyExceptionObj.itemName = this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.MARGIN');
      policyExceptionObj.actual = this.costMarginExceptions ? this.costMarginExceptions.estimatedMargin.toString() : this.constants.ZERO_DECIMAL;
      policyExceptionObj.policy = this.costMarginExceptions ? this.costMarginExceptions.estimatedMarginMinimum.toString() : this.constants.ZERO_DECIMAL;
      policyExceptionObj.variance = variance.toString();
      policyExceptionObj.reasonForException = this.costMarginExceptions?.reasonForException || Constants.EMPTY_STRING;
      policyExceptionObj.approver = this.constants.SMorBGM;
      policyExceptionObj.approverEmailId = this.costMarginExceptions?.approverEmailId || Constants.EMPTY_STRING;
      this.costReviewProjectMgmtRequirements?.push(policyExceptionObj);
    }

    if (this.isEngineerExceptionApplicable) {
      let policyExceptionObj = new SolPolicyExceptionData();
      policyExceptionObj.itemName = this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.ENGINEERING_TDC');
      policyExceptionObj.actual = costPlannedCostLaborEngineerObj ? costPlannedCostLaborEngineerObj?.tdcPercent.toString() : this.constants.ZERO_DECIMAL;
      policyExceptionObj.policy = costPlannedCostLaborEngineerObj ? costPlannedCostLaborEngineerObj?.target.toString() : this.constants.ZERO_DECIMAL;
      policyExceptionObj.variance = this.calculateVariance(costPlannedCostLaborEngineerObj);
      policyExceptionObj.reasonForException = this.engineerReasonForException;
      policyExceptionObj.approver = this.constants.ZOM;
      policyExceptionObj.approverEmailId = this.engineerApproverEmail;
      this.costReviewProjectMgmtRequirements?.push(policyExceptionObj);
    }
    if (this.costMarginExceptions?.escalationApplied) {
      let policyExceptionObj = new SolPolicyExceptionData();
      policyExceptionObj.itemName = this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.ESCALATION');
      policyExceptionObj.actual = this.constants.NONE;
      policyExceptionObj.policy = this.costMarginExceptions.escalationApplied;
      policyExceptionObj.variance = this.appendCurrencySymbol(this.costMarginExceptions.escalationCost) || this.appendCurrencySymbol(this.constants.ZERO_DECIMAL) || this.constants.EMPTY_STRING;
      policyExceptionObj.reasonForException = this.constants.NA;
      policyExceptionObj.approver = this.constants.SMorBGM;
      policyExceptionObj.approverEmailId = this.constants.NA;
      this.costReviewProjectMgmtRequirements?.push(policyExceptionObj);
    }

    if (this.costSummary?.zteApprovalReceived && this.commonFunctionsService.isValueEqualToTranslation(this.getParameterValues(this.constants.YES_NO, this.costSummary?.zteApprovalReceived), 'YES')) {
      let policyExceptionObj = new SolPolicyExceptionData();
      policyExceptionObj.itemName = this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.ZTE_APPROVAL_REQUIRED');
      policyExceptionObj.actual = this.constants.NONE;
      policyExceptionObj.policy = this.getParameterValues(this.constants.YES_NO, this.costSummary.zteApprovalReceived);
      policyExceptionObj.variance = this.constants.NA;
      policyExceptionObj.reasonForException = this.constants.NA;
      policyExceptionObj.approver = this.constants.NA;
      policyExceptionObj.approverEmailId = this.constants.NA;
      this.costReviewProjectMgmtRequirements?.push(policyExceptionObj);
    }
    // Backend string: accept the English constant as well as the current-culture translation.
    const taskBasedEstimating = this.costSummary?.fireTaskBasedEstimating;
    if (this.costSummary?.isFireTBEVisibility && (taskBasedEstimating === this.constants.NO || this.commonFunctionsService.isValueEqualToTranslation(taskBasedEstimating, 'NO'))) {
      let policyExceptionObj = new SolPolicyExceptionData();
      policyExceptionObj.itemName = this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.TASK_BASED_ESTIMATING');
      policyExceptionObj.actual = this.constants.NONE;
      policyExceptionObj.policy = this.costSummary.fireTaskBasedEstimating;
      policyExceptionObj.variance = this.constants.NA;
      policyExceptionObj.reasonForException = this.constants.NA;
      policyExceptionObj.approver = this.constants.SM;
      policyExceptionObj.approverEmailId = this.constants.NA;
      this.costReviewProjectMgmtRequirements?.push(policyExceptionObj);
    }
    const prefabControllerUsed = this.costPrefabPanel?.prefabControllerUsed;
    const isPrefabControllerUsedYes = prefabControllerUsed === this.constants.YES || this.commonFunctionsService.isValueEqualToTranslation(prefabControllerUsed, 'YES');
    if (this.costPrefabPanel && this.costPrefabPanel.isPrefabControllerVisible && !isPrefabControllerUsedYes) {
      let policyExceptionObj = new SolPolicyExceptionData();
      policyExceptionObj.itemName = this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.PRE_FAB_CONTROLLER_USED');
      policyExceptionObj.actual = this.costPrefabPanel.prefabControllerUsed;
      policyExceptionObj.policy = this.constants.YES;
      policyExceptionObj.variance = this.constants.NA;
      policyExceptionObj.reasonForException = this.costPrefabPanel.reasonForException;
      policyExceptionObj.approver = this.constants.NA;
      policyExceptionObj.approverEmailId = this.constants.NA;
      this.costReviewProjectMgmtRequirements?.push(policyExceptionObj);

    }
  }

  calculateVariance(costPlannedCostLaborEngineerObj: CostReviewPlannedCosts | undefined): string {
    if (costPlannedCostLaborEngineerObj) {
      let variance = costPlannedCostLaborEngineerObj.riskDisplayText - costPlannedCostLaborEngineerObj.tdcPercent;
      return variance.toString();
    }
    return this.constants.ZERO_DECIMAL;
  }

  appendCurrencySymbol(value: string | number): string | null {
    return this.multilingualCurrencyPipe.transform(value);
  }

  getParameterValues(key: string, value: string | number | undefined): string {
    return this.reviewService.getCostReviewParametersDescription(key, value);
  }

  isNumber(value: string | number): boolean {
    return !isNaN(Number(value));
  }

  exportToPdf(selectedReview: ReviewDetails, tabName: string) {
    if (tabName === Constants.SOL_POLICY_EXCEPTIONS) {
      this.translateHeaders();
      if (!this.costReviewProjectMgmtRequirements || this.costReviewProjectMgmtRequirements.length === Constants.ZERO) {

        const options: ExportObjectAsFieldValuePDFOptions = {
          data: { [this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.POLICY_EXCEPTIONS_DATA_NOT_FOUND')]: Constants.EMPTY_STRING },
          title: Constants.EMPTY_STRING,
          fileName: 'policy-exception.pdf',
          estimateID: selectedReview.estimateID,
          estimateName: selectedReview.request,
          tabName: this.translate.instant(`REVIEW_SOL_TABS_LABELS.POLICY_EXCEPTIONS`),
        };
        this.commonFunctionsService.exportObjectAsFieldValuePDF(options);
        return;
      }

      const formattedData = this.costReviewProjectMgmtRequirements.map(row => {
        const isMarginOrEngTDC =
          row.itemName === this.constants.MARGIN ||
          row.itemName === this.constants.ENGINEERING_TDC;
        let actual = row.actual;
        let policy = row.policy;
        let variance = row.variance;

        switch (row.itemName) {
          case this.constants.MARGIN:
          case this.constants.ENGINEERING_TDC:
            if (actual) {
              actual = `${this.decimalPrecision.transform(actual)}%`;
            }
            if (policy) {
              policy = `${this.decimalPrecision.transform(policy)}%`;
            }
            if (variance) {
              variance = `${this.decimalPrecision.transform(variance)}%`;
            }
            break;
          default:
        }

        return {
          ...row,
          actual,
          policy,
          variance
        };
      });

      const options: ExportTablesToPDFOptions = {
        tables :  [{
          title: this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.TITLE'),
          displayedColumns: this.columns.map(col => col.field),
          headers: this.translateHeaders(),
          data: formattedData,
          typeGuard: (row: SolPolicyExceptionData, col: string) => row[col as keyof SolPolicyExceptionData]
        }],
        fileName: 'policy-exception.pdf',
        hasNotes: false,
        estimateID: selectedReview.estimateID,
        estimateName: selectedReview.request,
        tabName: this.translate.instant('REVIEW_SOL_TABS_LABELS.POLICY_EXCEPTIONS'),
      };
      this.commonFunctionsService.exportTablesToPDF(options);
    }
  }

  translateHeaders(): { [key: string]: string } {
    let headers = {
      itemName: this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.ITEM'),
      actual: this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.ACTUAL'),
      policy: this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.POLICY'),
      variance: this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.VARIANCE'),
      approver: this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.APPROVER'),
      approverEmailId: this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.APPROVER_EMAIL'),
      reasonForException: this.translate.instant('REVIEW_SOL_POLICY_EXCEPTIONS_TAB.REASON_FOR_EXCEPTION')
    };
    return headers;
  }

}
